import styles from './style.postcss';

import React, { Component } from 'react';
import invariant from 'invariant';
import PropTypes from 'prop-types';

import {
  PageCard,
  Grid,
  Button,
  connect,
  moment,
  ConfirmDialog,
  CalendarPicker,
  testClass,
} from 'omni-common-ui';
import ExpansionButton from 'components/ExpansionButton';
import ReadOnlyField from '../ReadOnlyField';
import {
  actions as idpActions,
} from 'data/IndividualDevelopmentPlans';
import { bindActionCreators } from 'redux';

const FOR_PERIOD = 'For Period';
const NAME = 'Name';
const TEACHER_ONE = 'Teacher 1';
const COURSE = 'Course';
const TEACHER_TWO = 'Teacher 2';
const CENTRE = 'Centre';
const PROGRESS_ADVISOR = 'Progress Advisor';
const START_OF_DAY = 'START_OF_DAY';
const END_OF_DAY = 'END_OF_DAY';
const PERIOD_FROM = 'periodFrom';
const PERIOD_TO = 'periodTo';

class StudentInformation extends Component {
  constructor(props) {
    super(props);
    const { start, end } = this.props.studentInfo.period;
    this.state = {
      periodFrom: this._getLocalDateFromMoment(start, START_OF_DAY),
      periodTo: this._getLocalDateFromMoment(end, END_OF_DAY),
      wasDateChangeMade: false,
      shouldShowConfirmDialog: false,
    };
    this.studentInfoDisplayItems = [];

    this._onPeriodUpdated = this._onPeriodUpdated.bind(this);
    this._onUpdateClicked = this._onUpdateClicked.bind(this);
  }

  componentWillReceiveProps(props) {
    if (props.studentInfo &&
        (props.studentInfo.period.start !== this.props.studentInfo.period.start ||
        props.studentInfo.period.end !== this.props.studentInfo.period.end)) {
      const { start, end } = props.studentInfo.period;
      this.setState({
        periodFrom: this._getLocalDateFromMoment(start, START_OF_DAY),
        periodTo: this._getLocalDateFromMoment(end, END_OF_DAY),
        wasDateChangeMade: false,
      });
    }
  }

  _getLocalDateFromMoment(date, type) {
    invariant(moment.isMoment(date), '<StudentInformation /> can only receive the period as moment object');
    switch (type) {
      case START_OF_DAY:
        return date.clone().tz(this.props.timeZone).startOf('day').toDate();
      case END_OF_DAY:
        return date.clone().tz(this.props.timeZone).endOf('day').toDate();
      default:
        return date.clone().tz(this.props.timeZone).toDate();
    }
  }

  _getUtcDateString(date) {
    return moment.utc(date).format('YYYY-MM-DDTHH:mm:ss');
  }

  _getDisplayDate(date) {
    const momentDate = moment.utc(date).clone().tz(this.props.timeZone);
    const year = momentDate.year();
    const month = momentDate.month();
    const day = momentDate.date();
    return new Date(year, month, day);
  }

  _renderGridItems() {
    return this.studentInfoDisplayItems.map((source, index) => {
      if (index === 0 && this.props.canEdit) return this._renderPeriodPickers();
      let size = {};
      size = index === 0 ? { xs: 12, sm: 12, md: 12 } : { xs: 12, sm: 6, md: 3 };
      return <Grid.Item xs={size.xs}
          sm={size.sm}
          md={size.md}
          className={styles.StudentInformation_item}
          key={source.title}>
        <ReadOnlyField boardTitle={source.title}
            boardValue={source.value}
            className={source.className}
            hideDivider={source.hideDivider} />
      </Grid.Item>;
    });
  }

  _renderConfirmDialog() {
    const returnToView = () => this.setState({ shouldShowConfirmDialog: false });
    const discardUpdate = () => {
      const { start, end } = this.props.studentInfo.period;
      this.setState({
        periodFrom: this._getLocalDateFromMoment(start, START_OF_DAY),
        periodTo: this._getLocalDateFromMoment(end, END_OF_DAY),
        wasDateChangeMade: false,
      });
      returnToView();
    };
    return <ConfirmDialog isOpen={this.state.shouldShowConfirmDialog}
        primaryButtonContent="Update"
        secondaryButtonContent="Discard"
        onPrimaryClick={() => this._onPeriodUpdated()}
        onSecondaryClick={discardUpdate}
        onRequestClose={returnToView}>
      <span>
        This will update the scores and clear all previous edits.
      </span>
    </ConfirmDialog>;
  }

  _shouldDisableUpdateButton() {
    return this.props.isInEditMode || ! this.state.wasDateChangeMade;
  }

  _renderPeriodPickers() {
    const { groupStartDate, groupEndDate } = this.props;
    const { periodFrom, periodTo } = this.state;
    if (groupStartDate && groupEndDate) {
      const disabledStartDate = this._getLocalDateFromMoment(groupStartDate);
      const periodFromLowerBound = this.props.periodFromMinDate
        ? this._getLocalDateFromMoment(this.props.periodFromMinDate)
        : disabledStartDate;
      let disabledEndDate = this._getLocalDateFromMoment(groupEndDate);
      const currentDay = moment().clone().tz(this.props.timeZone).toDate();
      if (disabledEndDate.getTime() > currentDay.getTime()) disabledEndDate = currentDay;
      return <Grid.Item key="calenderPickers"
          xs={12}
          sm={12}
          md={12}
          className={styles.StudentInformation_item}>
        <div className={styles.StudentInformation_periodWrapper}>
          <div className={styles.StudentInformation_periodWrapper_itemContainer}>
            <div className={styles.StudentInformation_periodWrapper_item}>
              <span className={styles.StudentInformation_periodWrapper_item_label}>
                For Period From
              </span>
              <CalendarPicker day={this._getDisplayDate(periodFrom)}
                  onDateChanged={(day) => this._onDateChanged(PERIOD_FROM, day)}
                  disabledDayBefore={periodFromLowerBound}
                  disabledDayAfter={periodTo}
                  isValid />
            </div>
            <div className={styles.StudentInformation_periodWrapper_item}>
              <span className={styles.StudentInformation_periodWrapper_item_label}>To</span>
              <CalendarPicker day={this._getDisplayDate(periodTo)}
                  onDateChanged={(day) => this._onDateChanged(PERIOD_TO, day)}
                  disabledDayBefore={periodFrom}
                  disabledDayAfter={disabledEndDate}
                  isValid />
            </div>
            <Button type={Button.Type.primary}
                onClick={this._onUpdateClicked}
                className={styles.StudentInformation_button}
                disabled={this._shouldDisableUpdateButton()}>
              UPDATE
            </Button>
          </div>
        </div>
      </Grid.Item>;
    }
  }

  _onUpdateClicked() {
    this.setState({ shouldShowConfirmDialog: true });
  }

  _closeConfirmDialog() {
    this.setState({ shouldShowConfirmDialog: false });
  }

  _onPeriodUpdated() {
    const period = {
      fromTime: this._getUtcDateString(this.state.periodFrom),
      toTime: this._getUtcDateString(this.state.periodTo),
    };
    this.props.submitIdpPeriod(this.props.idpId, period, this.props.groupIdpId);
    this.setState({ wasDateChangeMade: false });
    this._closeConfirmDialog();
  }

  _onDateChanged(changedType, date) {
    let startOrEndOfDay;
    if (changedType === PERIOD_FROM) startOrEndOfDay = START_OF_DAY;
    if (changedType === PERIOD_TO) startOrEndOfDay = END_OF_DAY;
    const periodDate = this._getLocalDateFromMoment(moment.utc(date), startOrEndOfDay);
    this.setState({
      [changedType]: periodDate,
      wasDateChangeMade: true,
    });
  }

  _prepareStudentInfoDisplayItems() {
    this.studentInfoDisplayItems = [];
    const { isExpanded, studentInfo } = this.props;
    this.studentInfoDisplayItems.push({
      title: FOR_PERIOD,
      value: studentInfo.forPeriod,
      period: studentInfo.period,
      hideDivider: true,
    });
    if (isExpanded) {
      this.studentInfoDisplayItems.push({
        title: NAME,
        value: studentInfo.name,
        hideDivider: true,
      });
      this.studentInfoDisplayItems.push({
        title: COURSE,
        value: studentInfo.course,
        hideDivider: true,
      });
      this.studentInfoDisplayItems.push({
        title: TEACHER_ONE,
        value: studentInfo.teacherOne,
        hideDivider: true,
      });
      this.studentInfoDisplayItems.push({
        title: TEACHER_TWO,
        value: studentInfo.teacherTwo,
        hideDivider: true,
      });
      this.studentInfoDisplayItems.push({
        title: CENTRE,
        value: studentInfo.centre,
        hideDivider: true,
      });
      this.studentInfoDisplayItems.push({
        title: PROGRESS_ADVISOR,
        value: studentInfo.progressAdvisor,
        hideDivider: true,
      });
    }
  }

  _renderExpansionButton() {
    const { isExpanded, onExpandButtonClick } = this.props;
    return <ExpansionButton isExpand={isExpanded}
        onExpansionButtonClick={() => onExpandButtonClick()} />;
  }

  render() {
    this._prepareStudentInfoDisplayItems.call(this);

    return <div className={testClass('student-information')}>
      <PageCard className={styles.StudentInformation_container}>
        <PageCard.Heading text="Student information" />
        <PageCard.Content className={styles.StudentInformation_content}>
          <Grid className={styles.StudentInformation_grid}>
            <Grid.Group>
              {this._renderGridItems()}
            </Grid.Group>
          </Grid>
        </PageCard.Content>
        {this._renderExpansionButton()}
      </PageCard>
      {this._renderConfirmDialog()}
    </div>;
  }
}

StudentInformation.propTypes = {
  studentInfo: PropTypes.object.isRequired,
  timeZone: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onExpandButtonClick: PropTypes.func.isRequired,
  submitIdpPeriod: PropTypes.func.isRequired,
  idpId: PropTypes.string.isRequired,
  groupIdpId: PropTypes.string.isRequired,
  groupStartDate: PropTypes.object.isRequired,
  groupEndDate: PropTypes.object.isRequired,
  periodFromMinDate: PropTypes.object,
  isInEditMode: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool,
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators(idpActions, dispatch);
}

export default connect(null, mapDispatchToProps)(StudentInformation);



// WEBPACK FOOTER //
// ./containers/Group/ProgressReportHFv4/IndividualDevelopmentPlanDetailView/StudentInformation/index.jsx